import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Edit2, Trash2, Book, Baby, BookOpen, GraduationCap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "ECD", label: "ECD (ECDA & ECDB)", icon: Baby, color: "text-purple-500", bg: "bg-purple-50", border: "border-purple-200" },
  { key: "Junior", label: "Junior (Grade 1 & 2)", icon: BookOpen, color: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200" },
  { key: "Senior", label: "Senior (Grade 3–7)", icon: GraduationCap, color: "text-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200" },
];

export default function SubjectsPage() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ECD");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    max_score: "100",
  });

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subjects`);
      const subjectList = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.subjects) ? response.data.subjects : []);
      setSubjects(subjectList);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      toast.error("Failed to load subjects");
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const tabSubjects = subjects.filter((s) => s.grade_group === activeTab);
  const currentTab = TABS.find((t) => t.key === activeTab);

  const handleOpenDialog = (subject = null) => {
    if (subject) {
      setEditingSubject(subject);
      setFormData({
        name: subject.name,
        code: subject.code || "",
        description: subject.description || "",
        max_score: subject.max_score?.toString() || "100",
      });
    } else {
      setEditingSubject(null);
      setFormData({ name: "", code: "", description: "", max_score: "100" });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubject(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Subject name is required");
      return;
    }
    try {
      if (editingSubject) {
        await axios.put(`${API}/subjects/${editingSubject.id}`, {
          ...formData,
          grade_group: activeTab,
        });
        toast.success("Subject updated successfully");
      } else {
        await axios.post(`${API}/subjects`, {
          ...formData,
          grade_group: activeTab,
        });
        toast.success("Subject created successfully");
      }
      handleCloseDialog();
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save subject");
    }
  };

  const handleDelete = async (subjectId) => {
    try {
      await axios.delete(`${API}/subjects/${subjectId}`);
      toast.success("Subject deleted/deactivated successfully");
      fetchSubjects();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete subject");
    }
  };

  const handleToggleActive = async (subject) => {
    try {
      await axios.put(`${API}/subjects/${subject.id}`, {
        is_active: !subject.is_active,
      });
      toast.success(subject.is_active ? "Subject deactivated" : "Subject activated");
      fetchSubjects();
    } catch (error) {
      toast.error("Failed to update subject");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="subjects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-3">
            <Book className="text-pink-500" size={28} />
            Subjects Management
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Add and manage subjects for each grade group
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const count = subjects.filter((s) => s.grade_group === tab.key && s.is_active).length;
          return (
            <Card key={tab.key} className={`border-slate-200 cursor-pointer transition-all ${activeTab === tab.key ? `ring-2 ring-pink-300 ${tab.bg}` : "hover:bg-slate-50"}`} onClick={() => setActiveTab(tab.key)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Icon className={tab.color} size={20} />
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-xs text-slate-500">{tab.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tab Content */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              {currentTab && <currentTab.icon size={20} className={currentTab.color} />}
              {currentTab?.label} Subjects ({tabSubjects.length})
            </CardTitle>
            <Button onClick={() => handleOpenDialog()} className="bg-pink-500 hover:bg-pink-600 text-white">
              <Plus size={16} className="mr-2" />
              Add Subject
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tabSubjects.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Book size={48} className="mx-auto mb-4 opacity-50" />
              <p>No subjects for {currentTab?.label} yet</p>
              <p className="text-sm mt-1">Click "Add Subject" to create one</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500">Name</TableHead>
                    <TableHead className="font-medium text-slate-500">Code</TableHead>
                    <TableHead className="font-medium text-slate-500">Description</TableHead>
                    <TableHead className="font-medium text-slate-500">Max Score</TableHead>
                    <TableHead className="font-medium text-slate-500">Status</TableHead>
                    <TableHead className="font-medium text-slate-500">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tabSubjects.map((subject) => (
                    <TableRow key={subject.id} className="table-row-hover">
                      <TableCell className="font-medium text-slate-900">{subject.name}</TableCell>
                      <TableCell className="text-slate-600">{subject.code || "—"}</TableCell>
                      <TableCell className="max-w-xs truncate text-slate-600">{subject.description || "—"}</TableCell>
                      <TableCell className="text-slate-600">{subject.max_score}</TableCell>
                      <TableCell>
                        <Badge variant={subject.is_active ? "default" : "secondary"}>
                          {subject.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleOpenDialog(subject)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant={subject.is_active ? "outline" : "default"} onClick={() => handleToggleActive(subject)}>
                            {subject.is_active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(subject)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSubject ? "Edit Subject" : `Add Subject — ${currentTab?.label}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Subject Name *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleFormChange} placeholder="e.g., Mathematics" required />
            </div>
            <div>
              <Label htmlFor="code">Subject Code</Label>
              <Input id="code" name="code" value={formData.code} onChange={handleFormChange} placeholder="e.g., MATH" />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input id="description" name="description" value={formData.description} onChange={handleFormChange} placeholder="Subject description" />
            </div>
            <div>
              <Label htmlFor="max_score">Maximum Score</Label>
              <Input id="max_score" name="max_score" type="number" value={formData.max_score} onChange={handleFormChange} step="1" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" className="bg-pink-500 hover:bg-pink-600">{editingSubject ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subject</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? If this subject has results, it will be deactivated instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-4 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm.id)} className="bg-destructive">Delete</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
